import { isFunction } from 'lodash';
import { assign, createMachine } from 'xstate';
import { updateUtilityAccount, fetchUtilityAccount, sendInstantAlert } from '~/services';
import { fetchTermsBase } from '~/services/fetch-vnm-terms/utils';
import { ContractVersion } from '~/typings/context';
import { fetchContractLanguage } from '~/utils/fetch-contract-language';
import { sendToSegment } from '~/utils/tracking';
import { setError } from '../utils/actions';
import { contextCreator } from '../utils/context';
import { ReviewContext, ReviewDoneData, ReviewEvent, ReviewServices, ReviewStates } from './types';

export const reviewMachine = () =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as ReviewContext,
        events: {} as ReviewEvent,
        services: {} as ReviewServices,
      },
      context: {
        errors: {},
        funnelPacket: {} as ReviewContext['funnelPacket'],
        showLmi: false,
        vnmSolarProjectContracts: [] as ContractVersion[],
        requiresAddressFields: false,
      },
      id: 'reviewMachine',
      initial: 'start',
      states: {
        start: {
          entry: 'trackStarted',
          always: { target: 'review' },
        },
        review: {
          initial: 'fetchingUtilityAccount',
          states: {
            fetchingUtilityAccount: {
              invoke: {
                id: 'fetchUtilityAccount',
                src: 'fetchUtilityAccount',
                onDone: [
                  {
                    cond: 'isNjUtilityAccountMissingAddress',
                    target: 'fetching',
                    actions: ['setUtilityAccount', 'setRequiresAddressFields'],
                  },
                  { target: 'fetching', actions: 'setUtilityAccount' },
                ],
                onError: { target: 'fetching', actions: 'setReviewError' },
              },
            },
            fetching: {
              invoke: {
                id: 'fetchTerms',
                src: 'fetchTerms',
                onDone: [
                  {
                    cond: 'isNjUtilityAccountMissingAddress',
                    target: 'idle',
                    actions: 'saveFetchTermsData',
                  },
                  {
                    target: 'idle',
                    actions: 'saveFetchTermsData',
                  },
                ],
                onError: { target: 'idle', actions: 'setReviewError' },
              },
            },
            idle: {
              on: {
                SUBMIT_TERMS: [
                  {
                    cond: 'isNjUtilityAccountMissingAddress',
                    actions: 'setContextData',
                    target: 'updateUtilityAccount',
                  },
                  { target: 'sendInstantAlert', actions: 'setContextData' },
                ],
              },
            },
            updateUtilityAccount: {
              invoke: {
                id: 'updateUtilityAccount',
                src: 'updateUtilityAccount',
                onDone: { target: 'sendInstantAlert' },
                onError: { target: 'sendInstantAlert', actions: 'setReviewError' },
              },
            },
            sendInstantAlert: {
              invoke: {
                id: 'sendInstantAlert',
                src: 'sendInstantAlert',
                onDone: { target: '#completed' },
                onError: { target: '#completed' },
              },
            },
          },
        },
        completed: {
          id: 'completed',
          type: 'final',
          data: (context): ReviewDoneData => ({
            docuSignContracts: context.docuSignContracts,
            vnmSolarProjectContracts: context.vnmSolarProjectContracts,
            selfAttestationData: context.selfAttestationData,
            lmiStatus: context.lmiStatus,
            address: context.address,
            addressSecondary: context.addressSecondary,
          }),
          entry: 'trackCompleted',
        },
      },
    },
    {
      guards: {
        isNjUtilityAccountMissingAddress: context => {
          const state = context.funnelPacket.utility.state.abbreviation;
          return state === 'NJ' ? requiresAddressFields(context) : false;
        },
      },
      actions: {
        saveFetchTermsData: assign((_context, event) => {
          return {
            docuSignContracts: event.data.docuSignContracts,
            showLmi: event.data.showLmi,
            vnmSolarProjectContracts: event.data.vnmSolarProjectContracts,
          };
        }),
        setContextData: assign((_context, event) => {
          return {
            selfAttestationData: event.data?.SelfAttestationData,
            lmiStatus: event.data?.lmiStatus,
            address: event.data?.address,
            addressSecondary: event.data?.addressSecondary,
          };
        }),
        setUtilityAccount: assign((_context, event) => {
          return {
            utilityAccount: event.data.utilityAccount,
          };
        }),
        setRequiresAddressFields: assign((_context, _event) => {
          return {
            requiresAddressFields: requiresAddressFields(_context),
          };
        }),
        setReviewError: assign((context, event) => ({
          errors: setError<ReviewStates>('review', context, event),
        })),
        trackCompleted: context => {
          sendToSegment({
            type: 'review_step_completed',
            contractIds: context.vnmSolarProjectContracts?.map(c => c.id) || [],
            funnelPacket: context.funnelPacket,
          });
        },
        trackStarted: context => {
          sendToSegment({
            type: 'review_step_started',
            funnelPacket: context.funnelPacket,
          });
        },
      },
      services: {
        fetchTerms: async context => {
          const contractTags = isFunction(context.contractTags)
            ? context.contractTags(context)
            : context.contractTags;

          const {
            docuSignContracts,
            unsignedSolarContracts,
            showLmiFromTerms: showLmi,
          } = await fetchTermsBase({
            stateId: context.funnelPacket.utility.state.id,
            utilityAccountId: context.funnelPacket.utilityAccount.id,
            ...(contractTags ? { contractTags } : {}),
          });

          const data = fetchContractLanguage(docuSignContracts, unsignedSolarContracts, showLmi);
          return data;
        },
        fetchUtilityAccount: async (context: ReviewContext) => {
          const utilityAccountId = context.funnelPacket.utilityAccount.id;
          const data = await fetchUtilityAccount({ utilityAccountId });
          return data;
        },
        updateUtilityAccount: async (context: ReviewContext) => {
          const { street, city, state, zipCode } = context.address || {};
          const utilityAccountId = context.funnelPacket.utilityAccount.id;
          const utilityAccountNumber =
            context.funnelPacket.utilityAccount.accountNumber ||
            context.utilityAccount?.accountNumber;
          await updateUtilityAccount({
            utilityAccountId,
            utilityAccountNumber: utilityAccountNumber!,
            addressSecondary: context.addressSecondary,
            street,
            city,
            state,
            zipCode,
          });
          return undefined;
        },
        sendInstantAlert: async (context: ReviewContext) => {
          const { funnelPacket } = context;
          const propertiesData = {
            /* eslint-disable camelcase */
            lmi_status_selection: context.lmiStatus,
            /* eslint-disable camelcase */
          };

          if (funnelPacket.utility.state.abbreviation === 'NJ') {
            await sendInstantAlert({
              utilityAccountId: context.funnelPacket.utilityAccount.id,
              event: 'nj_lmi_status',
              properties: JSON.stringify(propertiesData),
            });
          }
        },
      },
    },
  );

const requiresAddressFields = (context: ReviewContext) => {
  const { utilityAccount } = context;
  const isAddressPresent = Boolean(
    utilityAccount &&
      utilityAccount.serviceAddressStreetOne &&
      utilityAccount.serviceAddressCity &&
      utilityAccount.serviceAddressState &&
      utilityAccount.serviceAddressZip,
  );
  return !isAddressPresent;
};

export const reviewContext = contextCreator<ReviewContext, 'funnelPacket'>(reviewMachine);
